import React, { FC, useEffect, useState } from "react";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Control,
  Controller,
  useFieldArray,
  useForm,
  UseFormGetValues,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  CATEGORY_LANGUAGE_CODES,
  Category,
  CategoryLanguageCode,
  CategoryStatus,
  CategoryType,
  normalizeCategoryLanguageCode,
} from "@/domain/category.interface";
import { CategoryPayload } from "@/services/categories.service";

type LanguageFormEntry = {
  language: CategoryLanguageCode;
  name: string;
  description: string;
};

type ItemFormEntry = {
  _id?: string;
  icon: string;
  hexaColor: string;
  textColor: string;
  status: CategoryStatus;
  languages: LanguageFormEntry[];
};

type CategoryFormData = {
  type: CategoryType | "";
  status: CategoryStatus;
  phoneNumber: string;
  categories: ItemFormEntry[];
};

const STATUSES: CategoryStatus[] = ["ACTIVE", "INACTIVE"];

const HEX_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

const buildSchema = (isEdit: boolean) =>
  yup.object().shape({
    type: isEdit
      ? yup.string().optional()
      : yup.string().trim().required("Type is required"),
    status: yup
      .mixed<CategoryStatus>()
      .oneOf(STATUSES)
      .required("Status is required"),
    phoneNumber: yup.string().trim().optional(),
    categories: yup
      .array()
      .of(
        yup.object().shape({
          _id: yup.string().optional(),
          icon: yup
            .string()
            .trim()
            .optional()
            .transform((v) => (v === "" ? undefined : v)),
          hexaColor: yup
            .string()
            .trim()
            .optional()
            .transform((v) => (v === "" ? undefined : v))
            .test(
              "is-hex",
              "Must be a valid hex color (e.g. #FF00AA)",
              (v) => !v || HEX_REGEX.test(v)
            ),
          textColor: yup
            .string()
            .trim()
            .optional()
            .transform((v) => (v === "" ? undefined : v))
            .test(
              "is-hex",
              "Must be a valid hex color (e.g. #FFFFFF)",
              (v) => !v || HEX_REGEX.test(v)
            ),
          status: yup.mixed<CategoryStatus>().oneOf(STATUSES).required(),
          languages: yup
            .array()
            .of(
              yup.object().shape({
                language: yup
                  .mixed<CategoryLanguageCode>()
                  .oneOf(CATEGORY_LANGUAGE_CODES)
                  .required("Language code is required"),
                name: yup.string().trim().required("Name is required"),
                description: yup.string().trim().optional(),
              })
            )
            .min(1, "At least one language is required")
            .default([]),
        })
      )
      .default([]),
  });

const emptyLanguage = (): LanguageFormEntry => ({
  language: CATEGORY_LANGUAGE_CODES[0],
  name: "",
  description: "",
});

const emptyItem = (): ItemFormEntry => ({
  icon: "",
  hexaColor: "",
  textColor: "",
  status: "ACTIVE",
  languages: [emptyLanguage()],
});

const DEFAULT_VALUES: CategoryFormData = {
  type: "",
  status: "ACTIVE",
  phoneNumber: "",
  categories: [],
};

type CategoryModalProps = {
  open: boolean;
  onClose: () => void;
  category?: Category;
  initialType?: CategoryType;
  availableTypes: CategoryType[];
  onSave: (data: CategoryPayload & { _id?: string }) => void;
  isPending: boolean;
};

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const TabPanel: FC<TabPanelProps> = ({ children, index, value }) => {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" sx={{ pt: 2 }}>
      {children}
    </Box>
  );
};

type LanguagesFieldArrayProps = {
  control: Control<CategoryFormData>;
  itemIndex: number;
  disabled: boolean;
};

const LanguagesFieldArray: FC<LanguagesFieldArrayProps> = ({
  control,
  itemIndex,
  disabled,
}) => {
  const theme = useAppTheme();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `categories.${itemIndex}.languages`,
  });

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="labelLarge" component="div">
          Languages
        </Typography>
        <Button
          variantType="save"
          label="Add language"
          onClick={() => append(emptyLanguage())}
          disabled={disabled}
        />
      </Stack>

      {fields.length === 0 && (
        <Typography variant="bodyMedium" color="text.secondary">
          At least one language is required.
        </Typography>
      )}

      {fields.map((f, lIdx) => (
        <Box
          key={f.id}
          sx={{
            p: 1.5,
            border: "1px dashed",
            borderColor: theme.palette.outline.main,
            borderRadius: "8px",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="titleSmall" component="div">
              Language #{lIdx + 1}
            </Typography>
            <IconButton
              aria-label="Remove language"
              onClick={() => remove(lIdx)}
              disabled={disabled || fields.length <= 1}
              sx={{ color: theme.palette.error.main }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Controller
                name={`categories.${itemIndex}.languages.${lIdx}.language`}
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="Code"
                    fullWidth
                    required
                    disabled={disabled}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || ""}
                  >
                    {CATEGORY_LANGUAGE_CODES.map((code) => (
                      <MenuItem key={code} value={code}>
                        {code}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Controller
                name={`categories.${itemIndex}.languages.${lIdx}.name`}
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="Name"
                    fullWidth
                    required
                    disabled={disabled}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name={`categories.${itemIndex}.languages.${lIdx}.description`}
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="Description"
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={disabled}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || ""}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      ))}
    </Stack>
  );
};

const toPayload = (
  data: CategoryFormData,
  editing: Category | undefined,
  getValues: UseFormGetValues<CategoryFormData>
): CategoryPayload => {
  const raw = getValues();
  const payload: CategoryPayload = {
    status: data.status,
    categories: (data.categories || []).map((it, idx) => {
      const rawItem = raw.categories?.[idx];
      const entry: CategoryPayload["categories"][number] = {
        languages: (it.languages || []).map((l) => {
          const langEntry: CategoryPayload["categories"][number]["languages"][number] =
            {
              language: l.language,
              name: l.name.trim(),
            };
          if (l.description?.trim()) langEntry.description = l.description.trim();
          return langEntry;
        }),
        status: it.status,
      };
      if (rawItem?._id) entry._id = rawItem._id;
      if (it.icon?.trim()) entry.icon = it.icon.trim();
      if (it.hexaColor?.trim()) entry.hexaColor = it.hexaColor.trim();
      if (it.textColor?.trim()) entry.textColor = it.textColor.trim();
      return entry;
    }),
  };

  if (!editing && data.type) {
    payload.type = data.type as CategoryType;
  }
  if (data.phoneNumber?.trim()) {
    payload.phoneNumber = data.phoneNumber.trim();
  }
  return payload;
};

const CategoryModal: FC<CategoryModalProps> = ({
  open,
  onClose,
  category,
  initialType,
  availableTypes,
  onSave,
  isPending,
}) => {
  const theme = useAppTheme();
  const isEdit = Boolean(category);
  const [activeTab, setActiveTab] = useState(0);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<CategoryFormData>({
    resolver: yupResolver(buildSchema(isEdit)) as never,
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItem,
  } = useFieldArray({
    control,
    name: "categories",
  });

  useEffect(() => {
    if (!open) return;
    setActiveTab(0);
    if (category) {
      reset({
        type: (category.type as CategoryType) || "",
        status: (category.status as CategoryStatus) || "ACTIVE",
        phoneNumber: category.phoneNumber || "",
        categories: Array.isArray(category.categories)
          ? category.categories.map((it) => ({
              _id: it._id,
              icon: it.icon ?? "",
              hexaColor: it.hexaColor ?? "",
              textColor: it.textColor ?? "",
              status: (it.status as CategoryStatus) || "ACTIVE",
              languages: Array.isArray(it.languages)
                ? it.languages.map((l) => ({
                    language:
                      normalizeCategoryLanguageCode(
                        l.language as string | undefined
                      ) ?? CATEGORY_LANGUAGE_CODES[0],
                    name: l.name ?? "",
                    description: l.description ?? "",
                  }))
                : [emptyLanguage()],
            }))
          : [],
      });
    } else {
      reset({
        ...DEFAULT_VALUES,
        type: (initialType as CategoryType | undefined) ?? "",
      });
    }
  }, [category, initialType, open, reset]);

  const onSubmit = (data: CategoryFormData) => {
    const payload = toPayload(data, category, getValues);
    onSave({ ...payload, _id: category?._id });
  };

  const typeOptions = isEdit
    ? category?.type
      ? [category.type]
      : []
    : availableTypes;

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      theme={theme}
      title={isEdit ? "Edit Category" : "Create Category"}
      size={{
        width: "100%",
        minHeight: 560,
        maxHeight: 820,
        maxWidth: 880,
      }}
      content={
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="General" />
            <Tab label={`Items (${itemFields.length})`} />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Type"
                      fullWidth
                      required={!isEdit}
                      disabled={isEdit || isPending}
                      error={!!errors.type}
                      helperText={errors.type?.message || ""}
                    >
                      {typeOptions.length === 0 && (
                        <MenuItem value="" disabled>
                          No types available
                        </MenuItem>
                      )}
                      {typeOptions.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Status"
                      fullWidth
                      required
                      error={!!errors.status}
                      helperText={errors.status?.message || ""}
                      disabled={isPending}
                    >
                      {STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      label="Phone number"
                      placeholder="+15551234567"
                      fullWidth
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber?.message || ""}
                      disabled={isPending}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="labelLarge" component="div">
                Items
              </Typography>
              <Button
                variantType="save"
                label="Add item"
                onClick={() => appendItem(emptyItem())}
                disabled={isPending}
              />
            </Stack>

            <Stack spacing={2}>
              {itemFields.length === 0 && (
                <Typography variant="bodyMedium" color="text.secondary">
                  No items added.
                </Typography>
              )}

              {itemFields.map((f, idx) => (
                <Box
                  key={f.id}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: theme.palette.outline.main,
                    borderRadius: "8px",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="titleSmall" component="div">
                      Item #{idx + 1}
                    </Typography>
                    <IconButton
                      aria-label="Remove item"
                      onClick={() => removeItem(idx)}
                      disabled={isPending}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`categories.${idx}.icon`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            label="Icon URL"
                            fullWidth
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`categories.${idx}.status`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            select
                            label="Item status"
                            fullWidth
                            required
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          >
                            {STATUSES.map((s) => (
                              <MenuItem key={s} value={s}>
                                {s}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`categories.${idx}.hexaColor`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            label="Background color (hex)"
                            placeholder="#FF00AA"
                            fullWidth
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`categories.${idx}.textColor`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            label="Text color (hex)"
                            placeholder="#FFFFFF"
                            fullWidth
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <LanguagesFieldArray
                        control={control}
                        itemIndex={idx}
                        disabled={isPending}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          </TabPanel>
        </Box>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default CategoryModal;
